import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTE_CONSTANTS } from "../constants/routeConstants";
import { DataContext } from "../dataContext";
import TransferDetails from "../components/TransferDetails";
import { ReactComponent as Info } from "../assets/ico_info.svg";
import BottomButton from "../components/BottomButton";
import { SpinnerCircularFixed } from "spinners-react";
import { useTransferApi } from "../api/transferApi";
import { isMobileDevice } from "../utils/deviceDetection";
import StepUpAuthModal from "../components/StepUpAuthModal";

const { TRANSFER_SUCCESSFUL, TRANSFER_FAILED } = ROUTE_CONSTANTS;

const TransferProcessing = ({ branding, homePath }) => {
  const navigate = useNavigate();
  const urlParams = useParams();
  const [transferStatus, setTransferStatus] = useState("PENDING");
  const [stepUpAuthRequired, setStepUpAuthRequired] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const { history, getHistory, notificationFeed, getNotificationsFeed } =
    useContext(DataContext);
  const { getTransferStatus } = useTransferApi();

  const transfer = history?.find(({ id }) => id === urlParams.id);
  const notification = notificationFeed?.find(
    ({ transfer_id }) => transfer_id === urlParams.id
    // notification/feed doesn't work locally
    // ({ transfer_id }) => transfer_id === "c35bc840-1865-5bf5-916d-b85ce20589c1" // off network WITH shortlink
    // ({ transfer_id }) => transfer_id === "c35bc840-1865-5bf5-916d-b85ce20589c2" // off network WITHOUT shortlink
    // ({ transfer_id }) => transfer_id === "17bfb8a4-2fa3-5615-b4b4-e3cc6151c65d" // on network succeeded
    // ({ transfer_id }) => transfer_id === "9754335f-7db7-5f10-9db0-79fd5b6fcb02" // on network failed
  );

  // Check for step-up auth requirement - using useCallback to avoid dependency issues
  const checkStepUpAuth = React.useCallback(async () => {
    try {
      // Don't make API calls if we don't have a valid transfer ID
      if (!urlParams.id) {
        return;
      }
      
      // Try to get transfer status, but handle errors gracefully
      let transferStatusData = null;
      try {
        transferStatusData = await getTransferStatus(urlParams.id);
      } catch (statusError) {
        // If the new endpoint doesn't exist yet, just continue with the old flow
        return;
      }
      
      // Check for step-up auth in various possible structures
      const isPending = transferStatusData.status === 'pending' || transferStatusData.status === 'PENDING';
      const events = transferStatusData.events || transferStatusData.getstream_events || [];
      
      if (isPending && events.length > 0) {
        // Look for step-up auth event
        const stepUpEvent = events.find(
          event => event.event === 'step_up_auth_needed'
        );
        
        // Check if shortlink_generated event exists (indicating step-up was completed)
        const shortlinkEvent = events.find(
          event => event.event === 'shortlink_generated'
        );
        
        // Only show step-up auth if it's needed AND shortlink hasn't been generated
        if (stepUpEvent && !shortlinkEvent) {
          // Check for verification URL in various possible locations
          const verificationUrl = stepUpEvent.verification_url || 
                                 stepUpEvent.data?.verification_url ||
                                 stepUpEvent.url ||
                                 stepUpEvent.data?.url;
          
          if (verificationUrl) {
            setStepUpAuthRequired(true);
            setVerificationUrl(verificationUrl);
            
            // Handle mobile vs desktop
            if (isMobileDevice()) {
              // Open verification URL in new window for mobile
              window.open(verificationUrl, '_blank');
            } else {
              // Show QR code modal for desktop
              setShowQrModal(true);
            }
          }
        }
      }
      
      // Check if transfer completed or failed
      const isCompleted = transferStatusData.status === 'COMPLETED' || transferStatusData.status === 'SUCCEEDED';
      const isFailed = transferStatusData.status === 'FAILED';
      
      if (isCompleted) {
        navigate(`${TRANSFER_SUCCESSFUL.replace(":id", urlParams.id)}`);
      } else if (isFailed) {
        navigate(`${TRANSFER_FAILED.replace(":id", urlParams.id)}`);
      }
    } catch (error) {
      console.error("Error checking step-up auth status:", error);
    }
  }, [getTransferStatus, navigate, urlParams.id]);

  // Initial data load on component mount
  useEffect(() => {
    getHistory();
    getNotificationsFeed();
  }, [getHistory, getNotificationsFeed]);

  useEffect(() => {
    const pollForResult = async () => {
      // If QR modal is showing, don't poll at all - user needs to complete auth externally
      if (showQrModal) {
        return;
      }
      
      // Try the new individual transfer endpoint first
      try {
        const transferStatusData = await getTransferStatus(urlParams.id);
        
        // Check for shortlink first - this indicates off-network completion
        const events = transferStatusData.events || transferStatusData.getstream_events || [];
        const shortlinkEvent = events.find(
          event => event.event === 'shortlink_generated' || event.type === 'shortlink_generated'
        );
        const hasShortlink = !!(shortlinkEvent?.url || shortlinkEvent?.data?.shortlink);

        // Request for Pay, on-network path: once the FSM hits
        // WaitForRequesteeApproval the requester is fully done — the
        // notification has been sent and we're now waiting on the requestee.
        // Treat it as "success from the requester's perspective" and
        // navigate forward.
        const isOnNetworkRequestReady =
          transferStatusData.originated_as_rfp === true &&
          transferStatusData.state === "WaitForRequesteeApproval";

        // If shortlink exists, transfer is ready - load data and navigate to success
        if (hasShortlink || isOnNetworkRequestReady) {
          // Ensure data is loaded for success page
          getHistory();
          getNotificationsFeed();
          navigate(`${TRANSFER_SUCCESSFUL.replace(":id", urlParams.id)}`);
          return;
        }
        
        // Check if transfer completed or failed (for on-network transfers)
        const isCompleted = transferStatusData.status === 'COMPLETED' || transferStatusData.status === 'SUCCEEDED';
        const isFailed = transferStatusData.status === 'FAILED';
        
        if (isCompleted) {
          // Ensure data is loaded for success page
          getHistory();
          getNotificationsFeed();
          navigate(`${TRANSFER_SUCCESSFUL.replace(":id", urlParams.id)}`);
          return;
        } else if (isFailed) {
          // Ensure data is loaded for failure page
          getHistory();
          getNotificationsFeed();
          navigate(`${TRANSFER_FAILED.replace(":id", urlParams.id)}`);
          return;
        }
        
        // Update transfer status for UI
        setTransferStatus(transferStatusData.status);
        
        // Check for step-up auth
        await checkStepUpAuth();
        
      } catch (error) {
        // Fallback to old polling logic if new endpoint fails
        getHistory();
        getNotificationsFeed();
        
        // Check for step-up auth
        await checkStepUpAuth();
        
        if (transfer) {
          setTransferStatus(transfer.status);
        }
        if (
          notification &&
          (notification?.status === "succeeded" || notification?.shortlink)
        ) {
          navigate(`${TRANSFER_SUCCESSFUL.replace(":id", urlParams.id)}`);
        }
        if (notification && notification?.status === "failed") {
          navigate(`${TRANSFER_FAILED.replace(":id", urlParams.id)}`);
        }
      }
    };
    
    if (transferStatus === "PENDING") {
      const interval = setInterval(() => {
        pollForResult();
      }, 3000);
      
      // Original timeout structure - keep step-up auth timeout longer
      const timeoutDuration = stepUpAuthRequired ? 300000 : 20000;
      const timeoutId = setTimeout(() => {
        // Don't timeout if QR modal is showing - user is completing auth
        if (!showQrModal) {
          clearInterval(interval);
          setTransferStatus("TIMEDOUT");
        }
      }, timeoutDuration);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeoutId);
      };
    }
  }, [
    getHistory,
    getNotificationsFeed,
    transfer,
    notification,
    transferStatus,
    urlParams,
    navigate,
    stepUpAuthRequired,
    showQrModal,
    checkStepUpAuth,
  ]);

  return (
    <>
      {transferStatus !== "TIMEDOUT" ? (
        <div className="center_page">
          {stepUpAuthRequired && !isMobileDevice() ? (
            <div style={{ textAlign: "center" }}>
              <h3 style={{ marginBottom: "20px" }}>Additional Authentication Required</h3>
              <p style={{ marginBottom: "30px" }}>
                Your financial institution requires additional authentication to complete this transfer.
              </p>
              <SpinnerCircularFixed
                color={branding.sending_colors.sending_bg_secondary}
                secondaryColor={branding.sending_colors.sending_bg_primary}
                speed={150}
                style={{ height: "60px", width: "60px" }}
              />
              <p style={{ marginTop: "20px", fontSize: "14px", color: "#666" }}>
                Please complete the authentication process...
              </p>
            </div>
          ) : (
            <>
              {stepUpAuthRequired && isMobileDevice() ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <SpinnerCircularFixed
                    color={branding.sending_colors.sending_bg_secondary}
                    secondaryColor={branding.sending_colors.sending_bg_primary}
                    speed={150}
                    style={{ height: "60px", width: "60px", marginBottom: "20px" }}
                  />
                  <div style={{ textAlign: "center", padding: "0 20px" }}>
                    <p style={{ marginBottom: "15px", fontSize: "16px", fontWeight: "500" }}>
                      Additional Authentication Required
                    </p>
                    <p style={{ marginBottom: "15px", fontSize: "14px", color: "#666" }}>
                      This transfer requires step-up authentication from your financial institution.
                      We've opened the authentication flow in a new tab.
                    </p>
                    <button
                      onClick={() => window.open(verificationUrl, '_blank')}
                    >
                      Complete Authentication
                    </button>
                    <p style={{ fontSize: "14px", color: "#666", marginBottom: "25px" }}>
                      Please complete the authentication process and return to this page.
                    </p>
                    <button 
                      onClick={() => navigate(ROUTE_CONSTANTS.MAIN_PAGE)}
                      style={{
                        padding: "12px 30px",
                        fontSize: "16px",
                        backgroundColor: branding.sending_colors.sending_bg_primary,
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer"
                      }}
                    >
                      Back to Send Money
                    </button>
                  </div>
                </div>
              ) : (
                <SpinnerCircularFixed
                  color={branding.sending_colors.sending_bg_secondary}
                  secondaryColor={branding.sending_colors.sending_bg_primary}
                  speed={150}
                  style={{ height: "60px", width: "60px" }}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <main className="page_without_nav">
          {transfer?.status ? (
            <div>
              <h3 data-cy="transfer_processed_message" className="status">
                {stepUpAuthRequired
                  ? "Authentication timed out. Please try your transfer again."
                  : "Your transfer is still processing. Please check the status in the \"History\" tab later."}
              </h3>
              {!stepUpAuthRequired && !transfer.on_network && (
                <div className="transfer_action_info">
                  <div style={{ marginRight: "10px" }}>
                    <Info
                      className="fill_sending_bg_primary stroke_sending_bg_secondary"
                    />
                  </div>
                  {transfer.recipient?.type === "phone" ? (
                    <>
                      You must return later to send a text message containing
                      the link required by the recipient to receive the funds.
                    </>
                  ) : (
                    <>
                      An email will be sent to the recipient once the transfer
                      has processed.
                    </>
                  )}
                </div>
              )}
              <hr style={{ margin: "20px 0" }} />
              {TransferDetails(transfer)}
            </div>
          ) : (
            <div className="center_page">Transfer Not Found</div>
          )}
          {BottomButton(() => navigate(homePath), "Return home")}
        </main>
      )}
      
      {/* Step-up Auth Modal */}
      {showQrModal && verificationUrl && (
        <StepUpAuthModal
          verificationUrl={verificationUrl}
          onClose={() => {
            setShowQrModal(false);
            // Navigate back to send money screen when cancelled
            navigate(ROUTE_CONSTANTS.MAIN_PAGE);
          }}
          onRefresh={() => {
            // Refresh just re-renders the QR code
            setShowQrModal(false);
            setTimeout(() => setShowQrModal(true), 100);
          }}
        />
      )}
    </>
  );
};

export default TransferProcessing;
